import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Github } from "lucide-react";
import { Logo } from "@/components/mdh/Nav";
import { useAuth, formatApiError } from "@/auth/AuthContext";
import { API, BACKEND } from "@/lib/api";

const OAUTH_ERRORS = {
    github_denied: "GitHub sign-in was cancelled — no account was created.",
    github_email_required:
        "Your GitHub account has no verified email address. Add one in GitHub settings and try again.",
};

const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
        <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.3h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.8z" />
        <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1l-3.9 3C3.2 21.3 7.3 24 12 24z" />
        <path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3l-3.9-3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.3l3.9-3z" />
        <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.2 2.7 1.3 6.7l3.9 3c.9-2.9 3.6-5 6.8-5z" />
    </svg>
);

const Field = ({ label, testid, ...props }) => (
    <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            {label}
        </label>
        <input
            data-testid={testid}
            {...props}
            className="mt-1.5 w-full rounded-md border border-slate-800 bg-slate-950/70 px-4 py-2.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none focus:ring-1 focus:ring-cyan-500/40"
        />
    </div>
);

export default function Auth({ mode: initialMode = "login" }) {
    const [mode, setMode] = useState(initialMode);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [phone, setPhone] = useState("");
    const [smsConsent, setSmsConsent] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);
    const { login, signup, loginWithGoogle } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const oauthError = OAUTH_ERRORS[searchParams.get("error")] || (searchParams.get("error") ? "Sign-in failed — please try again." : "");

    const loginWithGitHub = () => {
        window.location.href = `${BACKEND}/api/auth/github/start`;
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            if (mode === "forgot") {
                const r = await fetch(
                    `${BACKEND}/api/auth/forgot-password`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email }),
                    },
                );
                if (!r.ok) throw new Error("Request failed");
                setForgotSent(true);
            } else if (mode === "signup") {
                if (smsConsent && !phone.trim()) {
                    throw new Error("Enter your mobile number to enable SMS alerts.");
                }
                await signup(name, email, password, phone.trim(), smsConsent);
                navigate("/onboarding");
            } else {
                await login(email, password);
                navigate("/dashboard");
            }
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    const titles = {
        login: "Welcome back",
        signup: "Create your account",
        forgot: "Reset your password",
    };

    return (
        <div data-testid="auth-page" className="relative flex min-h-screen items-center justify-center px-5">
            <div className="bg-grid-slate pointer-events-none absolute inset-0 opacity-50" aria-hidden="true" />
            <div className="glow-hero pointer-events-none absolute inset-0" aria-hidden="true" />
            <div className="glass-panel relative w-full max-w-md rounded-xl p-8">
                <div className="flex items-center justify-between">
                    <Logo />
                    <Link
                        to="/"
                        data-testid="auth-back-link"
                        className="flex items-center gap-1.5 font-mono text-[11px] text-slate-500 hover:text-cyan-300"
                    >
                        <ArrowLeft className="h-3 w-3" /> Site
                    </Link>
                </div>

                <h1 className="font-display mt-8 text-2xl font-bold tracking-tight text-slate-100">
                    {titles[mode]}
                </h1>
                <p className="mt-2 text-xs leading-relaxed text-slate-500">
                    {mode === "signup" &&
                        "One question after this and your API is live."}
                    {mode === "login" && "Your command center missed you."}
                    {mode === "forgot" && "We'll email you a reset link — good for one hour."}
                </p>

                {mode !== "forgot" && (
                    <>
                        {oauthError && (
                            <p
                                data-testid="auth-oauth-error"
                                className="mt-6 rounded-md border border-rose-500/30 bg-rose-950/40 px-4 py-3 font-mono text-[11px] text-rose-300"
                            >
                                {oauthError}
                            </p>
                        )}
                        <button
                            data-testid="auth-google-button"
                            onClick={loginWithGoogle}
                            className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-md border border-slate-700 bg-slate-900/60 px-4 py-2.5 text-xs font-semibold text-slate-200 transition-colors hover:border-cyan-500/50 hover:text-cyan-300"
                        >
                            <GoogleIcon /> Continue with Google
                        </button>
                        <button
                            data-testid="auth-github-button"
                            onClick={loginWithGitHub}
                            className="mt-2.5 flex w-full items-center justify-center gap-2.5 rounded-md border border-slate-700 bg-slate-900/60 px-4 py-2.5 text-xs font-semibold text-slate-200 transition-colors hover:border-cyan-500/50 hover:text-cyan-300"
                        >
                            <Github className="h-4 w-4" /> Continue with GitHub
                        </button>
                        <div className="my-5 flex items-center gap-3">
                            <span className="h-px flex-1 bg-slate-800" />
                            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">
                                or
                            </span>
                            <span className="h-px flex-1 bg-slate-800" />
                        </div>
                    </>
                )}

                {forgotSent ? (
                    <div
                        data-testid="auth-forgot-sent"
                        className="mt-6 rounded-md border border-emerald-500/30 bg-emerald-950/40 px-4 py-3 font-mono text-xs text-emerald-300"
                    >
                        If that email exists, a reset link is on its way.
                    </div>
                ) : (
                    <form onSubmit={submit} className={mode === "forgot" ? "mt-6 space-y-4" : "space-y-4"}>
                        {mode === "signup" && (
                            <Field
                                label="Name"
                                testid="auth-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Ada Lovelace"
                                required
                            />
                        )}
                        <Field
                            label="Email"
                            testid="auth-email-input"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@company.dev"
                            required
                        />
                        {mode !== "forgot" && (
                            <Field
                                label="Password"
                                testid="auth-password-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={mode === "signup" ? "8+ characters" : "••••••••"}
                                minLength={mode === "signup" ? 8 : undefined}
                                required
                            />
                        )}
                        {mode === "signup" && (
                            <>
                                <label className="flex items-start gap-2.5">
                                    <input
                                        data-testid="auth-tos-checkbox"
                                        type="checkbox"
                                        required
                                        className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-cyan-400"
                                    />
                                    <span className="text-[11px] leading-relaxed text-slate-400">
                                        I agree to the{" "}
                                        <Link to="/terms" data-testid="auth-terms-link" className="text-cyan-300 underline underline-offset-2 hover:text-cyan-200">
                                            Terms of Service
                                        </Link>{" "}
                                        and acknowledge the{" "}
                                        <Link to="/privacy" data-testid="auth-privacy-link" className="text-cyan-300 underline underline-offset-2 hover:text-cyan-200">
                                            Privacy Policy
                                        </Link>
                                        .
                                    </span>
                                </label>

                                <div className="space-y-2.5 rounded-md border border-slate-800 bg-slate-950/50 p-3.5">
                                    <Field
                                        label="Mobile phone (optional)"
                                        testid="auth-phone-input"
                                        type="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="+1 555 123 4567"
                                    />
                                    <label className="flex items-start gap-2.5">
                                        <input
                                            data-testid="auth-sms-checkbox"
                                            type="checkbox"
                                            checked={smsConsent}
                                            onChange={(e) => setSmsConsent(e.target.checked)}
                                            className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-cyan-400"
                                        />
                                        <span className="text-[11px] font-semibold leading-relaxed text-slate-300">
                                            Yes — text me MDH-API alerts (optional)
                                        </span>
                                    </label>
                                    <p
                                        data-testid="auth-sms-disclosure"
                                        className="text-[10px] leading-relaxed text-slate-500"
                                    >
                                        If you opt in, MDH-API may send transactional and
                                        operational text messages related to your account and
                                        configured services, including webhook delivery alerts,
                                        endpoint and service status notifications, security alerts,
                                        and account notifications. Message frequency varies.
                                        Message and data rates may apply. Reply STOP to opt out or
                                        HELP for assistance. See our{" "}
                                        <Link to="/terms" className="text-cyan-400 underline underline-offset-2 hover:text-cyan-300">
                                            Terms of Service
                                        </Link>{" "}
                                        and{" "}
                                        <Link to="/privacy" className="text-cyan-400 underline underline-offset-2 hover:text-cyan-300">
                                            Privacy Policy
                                        </Link>
                                        . SMS consent is optional and is not required to create or
                                        use an MDH-API account.
                                    </p>
                                </div>
                            </>
                        )}
                        {error && (
                            <p data-testid="auth-error" className="font-mono text-[11px] text-rose-400">
                                {error}
                            </p>
                        )}
                        <button
                            data-testid="auth-submit-button"
                            type="submit"
                            disabled={busy}
                            className="w-full rounded-md bg-cyan-400 px-4 py-2.5 text-xs font-bold text-slate-950 shadow-[0_0_18px_rgba(6,182,212,0.3)] transition-colors hover:bg-cyan-300 disabled:opacity-50"
                        >
                            {busy
                                ? "One moment…"
                                : mode === "signup"
                                  ? "Create account"
                                  : mode === "forgot"
                                    ? "Send reset link"
                                    : "Log in"}
                        </button>
                    </form>
                )}

                <div className="mt-6 flex flex-wrap items-center justify-between gap-2 font-mono text-[11px] text-slate-500">
                    {mode === "login" && (
                        <>
                            <button
                                data-testid="auth-goto-signup"
                                onClick={() => { setMode("signup"); setError(""); }}
                                className="hover:text-cyan-300"
                            >
                                Need an account? Sign up
                            </button>
                            <button
                                data-testid="auth-goto-forgot"
                                onClick={() => { setMode("forgot"); setError(""); setForgotSent(false); }}
                                className="hover:text-cyan-300"
                            >
                                Forgot password?
                            </button>
                        </>
                    )}
                    {mode === "signup" && (
                        <button
                            data-testid="auth-goto-login"
                            onClick={() => { setMode("login"); setError(""); }}
                            className="hover:text-cyan-300"
                        >
                            Have an account? Log in
                        </button>
                    )}
                    {mode === "forgot" && (
                        <button
                            data-testid="auth-goto-login-2"
                            onClick={() => { setMode("login"); setError(""); setForgotSent(false); }}
                            className="hover:text-cyan-300"
                        >
                            Back to log in
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
